import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface LenderPartnershipPageProps {
    onBack: () => void;
}

interface LenderFormData {
    company: string;
    name: string;
    email: string;
    lendingFocus: string;
    dealSize: string;
}

const LenderPartnershipPage: React.FC<LenderPartnershipPageProps> = ({ onBack }) => {
    const [formData, setFormData] = useState<LenderFormData>({
        company: '',
        name: '',
        email: '',
        lendingFocus: '',
        dealSize: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');
        
        try {
            const partnershipData = {
                name: formData.name,
                email: formData.email,
                phone: '',
                company: formData.company,
                partnershipType: 'lender' as const,
                serviceArea: formData.lendingFocus,
                experience: formData.dealSize,
                certifications: '',
                website: '',
                additionalInfo: `Lending Focus: ${formData.lendingFocus}, Typical Deal Size: ${formData.dealSize}`
            };

            const response = await fetch('/api/partnership', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(partnershipData),
            });

            const result = await response.json();

            if (response.ok) {
                setSubmitStatus('success');
                // Reset form on successful submission
                setFormData({
                    company: '',
                    name: '',
                    email: '',
                    lendingFocus: '',
                    dealSize: ''
                });
            } else {
                console.error('Form submission error:', result.error);
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Network error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Lender Partnership Program</h1>
                    <p className="text-xl text-gray-600 mb-4">
                        Join our network of financial partners providing capital for NYC's green building transition.
                    </p>
                </div>

                {/* Hero Section */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl shadow-xl mb-12 overflow-hidden">
                    <div className="md:flex items-center">
                        <div className="md:w-1/2 p-8 md:p-12">
                            <h2 className="text-white text-3xl font-bold mb-4">Finance the Future of New York Real Estate</h2>
                            <p className="text-blue-100 text-lg mb-6">
                                Partner with us to fund LL97 retrofit projects with strong returns and environmental impact.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-center">
                                    <div className="bg-white rounded-full p-1 mr-3">
                                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-white">$5M+ average project size</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="bg-white rounded-full p-1 mr-3">
                                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-white">15-20% IRR on energy retrofit financing</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="bg-white rounded-full p-1 mr-3">
                                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-white">$250M+ pipeline of qualified projects</span>
                                </div>
                            </div>
                        </div>
                        <div className="md:w-1/2 bg-gradient-to-br from-blue-50 to-purple-50 p-6 hidden md:block">
                            <div className="w-full rounded-lg shadow-lg bg-white p-8 flex flex-col items-center justify-center text-center">
                                <div className="text-blue-600 mb-4">
                                    <svg className="w-24 h-24 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">NYC Energy Analytics</h3>
                                <p className="text-gray-600 text-sm">Real-time building performance data and energy compliance tracking for Local Law 97 requirements across NYC.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {/* Left Column */}
                    <div className="col-span-2 space-y-8">
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Why Partner With Us</h2>
                            <div className="space-y-6">
                                <div className="flex">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="bg-blue-100 rounded-full p-2">
                                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Pre-Qualified Deal Flow</h3>
                                        <p className="mt-1 text-gray-600">
                                            We've pre-qualified thousands of NYC buildings that need immediate LL97 retrofits, 
                                            creating a steady pipeline of financeable projects for our lending partners.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="bg-blue-100 rounded-full p-2">
                                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Simplified Underwriting</h3>
                                        <p className="mt-1 text-gray-600">
                                            Our platform provides detailed building analysis, compliance requirements, and retrofit ROI models, 
                                            reducing your underwriting time and costs by up to 70%.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="bg-blue-100 rounded-full p-2">
                                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Portfolio Diversification</h3>
                                        <p className="mt-1 text-gray-600">
                                            Green building finance offers stable, long-term returns with lower market correlation, 
                                            while meeting ESG requirements for investors and regulators.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Partnership Models</h2>
                            <div className="space-y-6">
                                <div className="border-l-4 border-green-500 pl-4">
                                    <h3 className="font-semibold text-gray-900">CPACE Financing</h3>
                                    <p className="text-gray-600 mt-1">Commercial Property Assessed Clean Energy financing with senior lien position. Ideal for large retrofit projects with 20-30 year terms.</p>
                                </div>
                                <div className="border-l-4 border-blue-500 pl-4">
                                    <h3 className="font-semibold text-gray-900">Green Construction Loans</h3>
                                    <p className="text-gray-600 mt-1">Short-term financing for energy efficiency retrofits with conversion to permanent financing upon completion.</p>
                                </div>
                                <div className="border-l-4 border-purple-500 pl-4">
                                    <h3 className="font-semibold text-gray-900">Equipment Leasing</h3>
                                    <p className="text-gray-600 mt-1">Financing for specific energy systems (HVAC, building envelope, etc.) with the equipment as collateral.</p>
                                </div>
                                <div className="border-l-4 border-orange-500 pl-4">
                                    <h3 className="font-semibold text-gray-900">Green Bonds & Securitization</h3>
                                    <p className="text-gray-600 mt-1">Portfolio-level financing for multiple building projects with standardized terms and reporting.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Call to Action */}
                    <div>
                        <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Become a Partner</h3>
                                <p className="text-gray-600">Join our network of financial partners and access NYC's growing market for building retrofits.</p>
                            </div>
                            
                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Institution Name</label>
                                    <input 
                                        type="text" 
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                                    <input 
                                        type="text" 
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input 
                                        type="email" 
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Lending Focus</label>
                                    <select 
                                        name="lendingFocus"
                                        value={formData.lendingFocus}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select lending focus</option>
                                        <option value="CPACE Financing">CPACE Financing</option>
                                        <option value="Green Construction Loans">Green Construction Loans</option>
                                        <option value="Equipment Leasing">Equipment Leasing</option>
                                        <option value="Green Bonds">Green Bonds</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Typical Deal Size</label>
                                    <select 
                                        name="dealSize"
                                        value={formData.dealSize}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select deal size</option>
                                        <option value="$500K - $2M">$500K - $2M</option>
                                        <option value="$2M - $10M">$2M - $10M</option>
                                        <option value="$10M - $50M">$10M - $50M</option>
                                        <option value="$50M+">$50M+</option>
                                    </select>
                                </div>
                                
                                {submitStatus === 'success' && (
                                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                                        Thank you! Your partnership request has been submitted successfully. We'll contact you within 24 hours.
                                    </div>
                                )}
                                
                                {submitStatus === 'error' && (
                                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                                        There was an error submitting your request. Please try again.
                                    </div>
                                )}
                                

                                
                                <p className="text-xs text-gray-500 text-center mt-2">
                                    We'll contact you within 24 hours with more information about our partnership program.
                                </p>
                            </form>
                            
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h4 className="font-medium text-gray-900 mb-2">Preferred Financing Partners</h4>
                                <div className="flex flex-wrap justify-center gap-4 mt-4">
                                    <div className="bg-green-50 border border-green-200 p-3 rounded-lg flex items-center gap-2">
                                        <div className="text-green-600">
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="font-semibold text-green-800 text-sm">CPACE Finance</span>
                                    </div>
                                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-center gap-2">
                                        <div className="text-blue-600">
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="font-semibold text-blue-800 text-sm">NYC Green Bank</span>
                                    </div>
                                    <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg flex items-center gap-2">
                                        <div className="text-purple-600">
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        </div>
                                        <span className="font-semibold text-purple-800 text-sm">Energy Finance</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Testimonials */}
                <div className="bg-gray-100 rounded-lg p-8 mb-12">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">What Our Partners Say</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center mb-4">
                                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl">
                                    JP
                                </div>
                                <div className="ml-4">
                                    <h4 className="font-semibold">James Peterson</h4>
                                    <p className="text-sm text-gray-600">NYC Green Bank</p>
                                </div>
                            </div>
                            <p className="text-gray-700 italic">"The pre-qualified deal flow has transformed our green lending program. We've seen a 40% increase in high-quality retrofit projects since partnering."</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center mb-4">
                                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold text-xl">
                                    SM
                                </div>
                                <div className="ml-4">
                                    <h4 className="font-semibold">Sarah Miller</h4>
                                    <p className="text-sm text-gray-600">Greenpath Capital</p>
                                </div>
                            </div>
                            <p className="text-gray-700 italic">"The analytics platform has cut our underwriting time in half while improving our risk assessment. Our CPACE portfolio has never performed better."</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center mb-4">
                                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-xl">
                                    RJ
                                </div>
                                <div className="ml-4">
                                    <h4 className="font-semibold">Robert Johnson</h4>
                                    <p className="text-sm text-gray-600">Metropolitan Lenders</p>
                                </div>
                            </div>
                            <p className="text-gray-700 italic">"Their building-specific LL97 analytics give us confidence in the regulatory drivers behind these retrofits. It's transformed our commercial lending strategy."</p>
                        </div>
                    </div>
                </div>

                {/* CTA Footer */}
                <div className="bg-blue-800 rounded-xl p-8 text-center">
                    <h2 className="text-2xl font-bold text-white mb-4">Ready to Finance NYC's Sustainable Future?</h2>
                    <p className="text-blue-100 mb-6 max-w-3xl mx-auto">
                        Join our network of lending partners today and gain access to qualified retrofit projects with strong returns and positive environmental impact.
                    </p>
                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-white text-blue-800 hover:bg-blue-100 disabled:bg-gray-200 py-3 px-8 rounded-lg font-medium text-lg transition-colors"
                    >
                        {isSubmitting ? 'Submitting...' : 'Schedule a Partnership Call'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LenderPartnershipPage;
